import '@qb/polyfills/browser-polyfills';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppContext, AppProps } from 'next/app';
import { userAgentFromString } from 'next/server';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { EmotionCacheProviderProps } from '@mui/material-nextjs/v14-pagesRouter';
import { ToastProvider } from '@qb/frontend/components/ToastProvider';
import { isBrowser } from '@qb/frontend/utils/env.utils';
import { getAPIUrlFromHost } from '@qb/frontend/utils/hosts/apiHost';
import { getHostFromReq } from '@qb/frontend/utils/hosts/getHostFromReq';
import { getHostFromWindow } from '@qb/frontend/utils/hosts/getHostFromWindow';
import { setSocketApiUrl } from '@qb/httpRequest/SocketRequest';
import { getTenantAliasFromHost } from '@/shared/TenantsAliasHelper';
import { NEXTJS_HAS_AUTHENTICATED_LAYOUT_COOKIE } from '@/src/components/Auth/hooks/useHasAuthenticatedLayoutCookie';
import { PagesRouterThemeProvider } from '@/src/components/Common/PagesRouterThemeProvider';
import ErrorBoundary from '@/src/components/Components/ErrorBoundary';
import { DeployRefreshManager } from '@/src/components/DeployRefreshManager/DeployRefreshManager';
import { AppLayout } from '@/src/components/Layouts/AppLayout/AppLayout';
import { ExternalScripts } from '@/src/components/Layouts/ExternalScripts';
import { UserManager } from '@/src/components/Layouts/UserManager';
import { DefaultSeoConfig } from '@/src/components/SEO/DefaultSEOConfig';
import { ModalContextProvider } from '@/src/contexts/CustomModalContext';
import { RightDrawerContextProvider } from '@/src/contexts/RightDrawerContext';
import { StoreProvider } from '@/src/store/StoreProvider';
import { queryClient } from '@/src/utils/queryClient';
type Props = AppProps & {
  hasAuthenticatedLayoutCookie: boolean;
  isUserAgentDeviceMobile: boolean;
  emotionCache: EmotionCacheProviderProps['emotionCache'];
  tenantAlias: string;
  baseApiURL: string;
};
function App({
  Component,
  hasAuthenticatedLayoutCookie,
  isUserAgentDeviceMobile,
  tenantAlias,
  baseApiURL,
  ...propsRest
}: Props) {
  const {
    pageProps,
    router
  } = propsRest;
  setSocketApiUrl(baseApiURL);
  return <>
      <DefaultSeoConfig data-sentry-element="DefaultSeoConfig" data-sentry-source-file="_app.tsx" />
      <StoreProvider data-sentry-element="StoreProvider" data-sentry-source-file="_app.tsx">
        <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
          <NuqsAdapter data-sentry-element="NuqsAdapter" data-sentry-source-file="_app.tsx">
            <UserManager data-sentry-element="UserManager" data-sentry-source-file="_app.tsx" />
            <PagesRouterThemeProvider isUserAgentDeviceMobile={isUserAgentDeviceMobile} emotionCache={propsRest.emotionCache} tenantAlias={tenantAlias} data-sentry-element="PagesRouterThemeProvider" data-sentry-source-file="_app.tsx">
              <ErrorBoundary data-sentry-element="ErrorBoundary" data-sentry-source-file="_app.tsx">
                <RightDrawerContextProvider data-sentry-element="RightDrawerContextProvider" data-sentry-source-file="_app.tsx">
                  <ModalContextProvider data-sentry-element="ModalContextProvider" data-sentry-source-file="_app.tsx">
                    <DeployRefreshManager data-sentry-element="DeployRefreshManager" data-sentry-source-file="_app.tsx" />
                    <AppLayout hasAuthenticatedLayoutCookie={hasAuthenticatedLayoutCookie} {...pageProps} data-sentry-element="AppLayout" data-sentry-source-file="_app.tsx">
                      <Component {...pageProps} {...propsRest} router={router} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
                    </AppLayout>
                  </ModalContextProvider>
                </RightDrawerContextProvider>
              </ErrorBoundary>
            </PagesRouterThemeProvider>
            <ReactQueryDevtools initialIsOpen={false} data-sentry-element="ReactQueryDevtools" data-sentry-source-file="_app.tsx" />
          </NuqsAdapter>
        </QueryClientProvider>
      </StoreProvider>
      <ToastProvider data-sentry-element="ToastProvider" data-sentry-source-file="_app.tsx" />
      <ExternalScripts data-sentry-element="ExternalScripts" data-sentry-source-file="_app.tsx" />
    </>;
}

/**
 * Using getInitialProps in App will disable Automatic Static Optimization.
 * https://nextjs.org/docs/pages/building-your-application/routing/custom-app#getinitialprops-with-app
 */
App.getInitialProps = async ({
  ctx
}: AppContext) => {
  const {
    req,
    query
  } = ctx;
  try {
    const hasAuthenticatedLayoutCookie = req?.headers.cookie?.includes(`${NEXTJS_HAS_AUTHENTICATED_LAYOUT_COOKIE}=`);
    const {
      device
    } = userAgentFromString(req?.headers['user-agent'] || '');

    // Get initial props runs both on client and server so we need different ways of getting tenantAlias.
    let tenantAlias;
    if (query?.tenantAlias) {
      tenantAlias = query?.tenantAlias;
    } else if (isBrowser) {
      // Fallback to client side parsing
      tenantAlias = getTenantAliasFromHost(window.location.hostname);
      // Fallback mostly for pages outside [tenantAlias] like 404 and _error
    } else if (req) {
      tenantAlias = getTenantAliasFromHost(getHostFromReq(req));
    }
    let baseApiURL;
    if (isBrowser) {
      baseApiURL = getAPIUrlFromHost(getHostFromWindow());
    } else if (req) {
      baseApiURL = getAPIUrlFromHost(getHostFromReq(req));
    }
    return {
      isUserAgentDeviceMobile: device.type === 'mobile',
      hasAuthenticatedLayoutCookie,
      tenantAlias,
      baseApiURL
    };
  } catch {
    return {
      hasAuthenticatedLayoutCookie: false,
      isUserAgentDeviceMobile: false
    };
  }
};
export default App;